import { useMemo, useState } from "react";
import { Checkbox, Color, Flex, Grid, Scroller, Search, Text } from "src/elements";
import { StoresSelectorStore } from "src/components/StoresSelector/store/types";
import { CheckboxProps } from "src/elements/Checkbox/Checkbox";
import { useTranslation } from "src/i18n";

type StoreColumnKey = "store" | "bay_size" | "business_type" | "activity_type";

const COLUMNS: { key: StoreColumnKey; labelKey: string }[] = [
  { key: "store", labelKey: "storesPickList.columnStore" },
  { key: "bay_size", labelKey: "storesPickList.columnBaySize" },
  { key: "business_type", labelKey: "storesPickList.columnBusinessType" },
  { key: "activity_type", labelKey: "storesPickList.columnActivityType" },
];

const GRID_COLUMNS = "24px repeat(4, 1fr)";
const GRID_COLUMNS_WITH_CDT = "24px repeat(4, 1fr) 1.6fr";
const ROW_PADDING = "6px 8px";

/** The CDT version a store resolves to in the flow's category. */
export interface StoreCdtVersion {
  treeId: number;
  label: string;
  isPinned: boolean;
}

interface Props {
  stores: StoresSelectorStore[];
  selectedCodes: Set<string>;
  setSelectedCodes: (updater: (prev: Set<string>) => Set<string>) => void;
  /**
   * Per store code, the version it uses. Present when the flow was started
   * from a CDT version: adds the "CDT version" column and the per-version
   * breakdown of the selected stores.
   */
  cdtVersions?: ReadonlyMap<string, StoreCdtVersion>;
}

export const StoresPickList = ({ stores, selectedCodes, setSelectedCodes, cdtVersions }: Props) => {
  const { t, tDynamic } = useTranslation("common");
  const [search, setSearch] = useState("");

  const visibleStores = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return stores;
    return stores.filter((s) => s.store.toLowerCase().includes(query));
  }, [stores, search]);

  const visibleCodes = useMemo(() => visibleStores.map((s) => s.store), [visibleStores]);
  const selectedVisibleCount = visibleCodes.filter((code) => selectedCodes.has(code)).length;

  // "#14 Spring reset × 7 · #2 ETL × 3" for the stores ticked so far.
  const versionBreakdown = useMemo(() => {
    if (!cdtVersions) return null;
    const counts = new Map<number, { label: string; count: number }>();
    for (const code of selectedCodes) {
      const version = cdtVersions.get(code);
      if (!version) continue;
      const entry = counts.get(version.treeId) ?? { label: version.label, count: 0 };
      entry.count += 1;
      counts.set(version.treeId, entry);
    }
    return [...counts.values()]
      .sort((a, b) => b.count - a.count)
      .map((entry) => `${entry.label} × ${entry.count}`)
      .join(" · ");
  }, [cdtVersions, selectedCodes]);

  const allVisibleChecked: CheckboxProps["isChecked"] =
    visibleCodes.length === 0 || selectedVisibleCount === 0
      ? false
      : selectedVisibleCount === visibleCodes.length
        ? true
        : "mixed";

  const toggleOne = (code: string, isChecked: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (isChecked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  const toggleAllVisible = (isChecked: boolean) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      for (const code of visibleCodes) {
        if (isChecked) {
          next.add(code);
        } else {
          next.delete(code);
        }
      }
      return next;
    });
  };

  const gridColumns = cdtVersions ? GRID_COLUMNS_WITH_CDT : GRID_COLUMNS;

  return (
    <Flex column gap="10px" flexGrow>
      <Flex justify="between" align="center" gap="10px">
        <Search
          search={search}
          setSearch={setSearch}
          placeholder={t("storesPickList.searchPlaceholder")}
          width="240px"
        />
        <Text variant="caption1">
          {t("storesPickList.selectedCount", {
            selected: String(selectedCodes.size),
            total: String(stores.length),
          })}
        </Text>
      </Flex>

      <Grid columns={gridColumns} gap="10px" align="center" padding={ROW_PADDING}>
        <Checkbox
          isChecked={allVisibleChecked}
          setIsChecked={toggleAllVisible}
          isCheckboxLeft
          isDisabled={visibleCodes.length === 0}
        />
        {COLUMNS.map((col) => (
          <Text key={col.key} variant="body1">
            {tDynamic(col.labelKey)}
          </Text>
        ))}
        {cdtVersions && <Text variant="body1">{t("storesPickList.columnCdtVersion")}</Text>}
      </Grid>

      <Scroller size="thin" maxHeight="320px">
        {visibleStores.map((store) => {
          const isSelected = selectedCodes.has(store.store);
          const version = cdtVersions?.get(store.store);

          return (
            <Grid
              key={store.store}
              columns={gridColumns}
              gap="10px"
              align="center"
              padding={ROW_PADDING}
              borderRadius="6px"
              background={isSelected ? Color.greenSmoke : undefined}
              backgroundHovered={isSelected ? Color.greenSmokeHover : Color.lightGray}
            >
              <Checkbox
                isChecked={isSelected}
                setIsChecked={(isChecked) => toggleOne(store.store, isChecked)}
                isCheckboxLeft
              />
              {COLUMNS.map((col) => (
                <Text key={col.key} variant="body2">
                  {store[col.key] ?? "-"}
                </Text>
              ))}
              {cdtVersions && (
                <Flex gap="6px" align="center">
                  <Text variant="body2">{version?.label ?? "-"}</Text>
                  {version && (
                    <Text
                      variant="caption1"
                      color={version.isPinned ? Color.infoBlueDark : Color.textSecondary}
                    >
                      {t(
                        version.isPinned ? "storesPickList.cdtPinned" : "storesPickList.cdtPrimary",
                      )}
                    </Text>
                  )}
                </Flex>
              )}
            </Grid>
          );
        })}
      </Scroller>

      {versionBreakdown !== null && (
        <Text variant="caption1" color={Color.textSecondary}>
          {versionBreakdown
            ? t("storesPickList.cdtVersionsInFlow", { list: versionBreakdown })
            : t("storesPickList.cdtVersionsNone")}
        </Text>
      )}
    </Flex>
  );
};
